import { useEffect, useMemo, useRef, useState } from 'react';
import useRoomState from '../useRoomState/useRoomState';
import useVideoContext from '../useVideoContext/useVideoContext';
import { decodeJoinToken, getJoinTokenFromUrl } from '../../utils/videoJoinToken';
import { fetchRoomSessionStart, registerRoomSessionStart } from '../../utils/sessionTimerApi';
import {
  FINAL_WINDOW_MINUTES,
  REMINDER_AT_ELAPSED_MINUTES,
  REMINDER_SHOWN_KEY,
  reminderShownStorageKey,
  SESSION_DURATION_MINUTES,
  SESSION_START_TIME_KEY,
  sessionStartStorageKey,
  TEST_REMINDER_AFTER_MINUTES,
} from './sessionTimerConfig';

export interface SessionTimerState {
  elapsedSeconds: number;
  remainingSeconds: number;
  isConnected: boolean;
  isInFinalWindow: boolean;
  isPastScheduledDuration: boolean;
  shouldShowReminder: boolean;
  acknowledgeReminder: () => void;
}

function resolveRoomId(roomName?: string | null): string {
  if (roomName && roomName.trim()) {
    return roomName.trim();
  }
  const token = getJoinTokenFromUrl();
  if (!token) {
    return '';
  }
  return decodeJoinToken(token)?.roomId?.trim() || '';
}

export default function useSessionTimer(testMode = false): SessionTimerState {
  const roomState = useRoomState();
  const { room } = useVideoContext();
  const isConnected = roomState === 'connected';
  const roomId = resolveRoomId(room?.name);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [shouldShowReminder, setShouldShowReminder] = useState(false);

  const sessionStartTimeRef = useRef<number | null>(null);
  const reminderShownRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncInFlightRef = useRef(false);

  useEffect(() => {
    if (!roomId) {
      return;
    }
    const startKey = sessionStartStorageKey(roomId);
    const reminderKey = reminderShownStorageKey(roomId);
    const storedStartTime = sessionStorage.getItem(startKey) || sessionStorage.getItem(SESSION_START_TIME_KEY);
    const storedReminderShown = sessionStorage.getItem(reminderKey) || sessionStorage.getItem(REMINDER_SHOWN_KEY);

    if (storedStartTime) {
      sessionStartTimeRef.current = parseInt(storedStartTime, 10);
      reminderShownRef.current = storedReminderShown === 'true';
    }
  }, [roomId]);

  useEffect(() => {
    if (!isConnected || !roomId) {
      return;
    }

    let cancelled = false;

    const applyStartTime = (epochMs: number) => {
      if (cancelled || !Number.isFinite(epochMs)) {
        return;
      }
      sessionStartTimeRef.current = epochMs;
      const startKey = sessionStartStorageKey(roomId);
      sessionStorage.setItem(startKey, String(epochMs));
      sessionStorage.removeItem(SESSION_START_TIME_KEY);
    };

    const syncSharedStartTime = async () => {
      if (syncInFlightRef.current) {
        return;
      }
      syncInFlightRef.current = true;
      try {
        let epochMs = await fetchRoomSessionStart(roomId);
        if (epochMs == null) {
          epochMs = await registerRoomSessionStart(roomId);
        }
        if (epochMs == null) {
          epochMs = await fetchRoomSessionStart(roomId);
        }
        if (epochMs != null) {
          applyStartTime(epochMs);
          return;
        }
      } finally {
        syncInFlightRef.current = false;
      }

      if (sessionStartTimeRef.current == null) {
        applyStartTime(Date.now());
      }
    };

    void syncSharedStartTime();
    const resyncTimer = window.setInterval(() => {
      void fetchRoomSessionStart(roomId).then(epochMs => {
        if (epochMs != null) {
          applyStartTime(epochMs);
        }
      });
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(resyncTimer);
    };
  }, [isConnected, roomId]);

  useEffect(() => {
    if (isConnected && intervalRef.current === null) {
      intervalRef.current = setInterval(() => {
        if (sessionStartTimeRef.current === null) {
          return;
        }

        const elapsedMs = Date.now() - sessionStartTimeRef.current;
        const elapsedMin = elapsedMs / (1000 * 60);
        const elapsedSec = Math.floor(elapsedMs / 1000);

        setElapsedSeconds(elapsedSec);

        const triggerReminder = testMode
          ? elapsedMin >= TEST_REMINDER_AFTER_MINUTES
          : elapsedMin >= REMINDER_AT_ELAPSED_MINUTES;

        if (triggerReminder && !reminderShownRef.current && roomId) {
          setShouldShowReminder(true);
          reminderShownRef.current = true;
          sessionStorage.setItem(reminderShownStorageKey(roomId), 'true');
          sessionStorage.removeItem(REMINDER_SHOWN_KEY);
        }
      }, 1000);
    }

    if (!isConnected && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setShouldShowReminder(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isConnected, roomId, testMode]);

  const remainingSeconds = Math.max(0, SESSION_DURATION_MINUTES * 60 - elapsedSeconds);
  const isInFinalWindow = isConnected && remainingSeconds > 0 && remainingSeconds <= FINAL_WINDOW_MINUTES * 60;
  const isPastScheduledDuration = isConnected && elapsedSeconds >= SESSION_DURATION_MINUTES * 60;

  const acknowledgeReminder = () => setShouldShowReminder(false);

  return useMemo(
    () => ({
      elapsedSeconds,
      remainingSeconds,
      isConnected,
      isInFinalWindow,
      isPastScheduledDuration,
      shouldShowReminder,
      acknowledgeReminder,
    }),
    [elapsedSeconds, remainingSeconds, isConnected, isInFinalWindow, isPastScheduledDuration, shouldShowReminder]
  );
}
